import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { attendanceService } from '@/services/attendanceService'
import type {
  Attendance,
  RecordAttendanceInput,
  BulkAttendanceInput,
  AttendanceSummary,
  UpdateAttendanceInput,
} from '@/types/attendance'
import type { ApiResponse } from '@/types/common'

/**
 * Hook to fetch attendance records for a specific session
 */
export function useSessionAttendance(sessionId: string) {
  return useQuery<ApiResponse<Attendance[]>, Error>({
    queryKey: ['attendance', 'session', sessionId],
    queryFn: () => attendanceService.getSessionAttendance(sessionId),
    enabled: !!sessionId,
  })
}

/**
 * Hook to fetch attendance records for a specific student in a batch
 */
export function useStudentAttendance(batchId: string, studentId: string) {
  return useQuery<ApiResponse<Attendance[]>, Error>({
    queryKey: ['attendance', 'batch', batchId, 'student', studentId],
    queryFn: () => attendanceService.getStudentAttendance(batchId, studentId),
    enabled: !!batchId && !!studentId,
  })
}

/**
 * Hook to fetch attendance summary for a batch
 */
export function useBatchAttendanceSummary(batchId: string) {
  return useQuery<ApiResponse<AttendanceSummary>, Error>({
    queryKey: ['attendance', 'batch', batchId, 'summary'],
    queryFn: () => attendanceService.getBatchAttendanceSummary(batchId),
    enabled: !!batchId,
  })
}

/**
 * Hook to record attendance for a single student
 */
export function useRecordAttendance() {
  const queryClient = useQueryClient()

  return useMutation<ApiResponse<Attendance>, Error, { sessionId: string; input: RecordAttendanceInput }>({
    mutationFn: ({ sessionId, input }) =>
      attendanceService.recordAttendance(sessionId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['attendance'] })
    },
  })
}

/**
 * Hook to record attendance for multiple students at once
 */
export function useBulkRecordAttendance() {
  const queryClient = useQueryClient()

  return useMutation<ApiResponse<Attendance[]>, Error, { sessionId: string; input: BulkAttendanceInput }>({
    mutationFn: ({ sessionId, input }) =>
      attendanceService.bulkRecordAttendance(sessionId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['attendance'] })
    },
  })
}

/**
 * Hook to update an existing attendance record
 */
export function useUpdateAttendance() {
  const queryClient = useQueryClient()

  return useMutation<ApiResponse<void>, Error, { id: string; input: UpdateAttendanceInput }>({
    mutationFn: ({ id, input }) =>
      attendanceService.updateAttendance(id, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['attendance'] })
    },
  })
}
